import { useState } from "react";
import { toast } from "sonner";
import { Zap, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { isGolfFormat, isGolfPickem, isSeasonFormat } from "@/lib/tournamentFormat";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

/**
 * iter35d — Admin settings for the Power Pick feature.
 * Football: enable/disable, mode (flat or handicap), flat multiplier.
 *
 * iter86.21 — Season-format aware (multiplier on total W/D/L).
 * iter86.31.1 — Golf pick'em aware: Power Pick is NOT a multiplier
 * there — it's a strokes handicap (N strokes shaved per round the
 * picked golfer actually plays, 1–5, default 2). Locks at R1 tee-off.
 */
export default function PowerPickSettingsCard({ tournament, isLocked, onChanged }) {
    const [busy, setBusy] = useState(false);
    if (!tournament) return null;
    const enabled = !!tournament.power_pick_enabled;
    const mode = tournament.power_pick_mode || "flat";
    const flat = tournament.power_pick_flat_multiplier || 1.5;
    const _isSeason = isSeasonFormat(tournament);
    const _isNfl = tournament.template === "nfl_season_api";
    const _isFootballPO = isFootballPlayoffsTemplate(tournament.template);
    const _pmSrc = tournament.pickem?.power_pick_multipliers || {};
    const _pmDef = _isNfl ? { 1: 1, 2: 1.25, 3: 1.5, 4: 2 } : _isFootballPO ? { 1: 1, 2: 1.25, 3: 1.5 } : { 1: 1, 2: 1.5, 3: 2 };
    const _pm = Object.fromEntries(Object.keys(_pmDef).map(k => [k, Number(_pmSrc[String(k)] ?? _pmDef[k])]));
    const _potListText = Object.keys(_pm).map(k => `Pot ${k} = ${_pm[k]}×`).join(" · ");
    const _isGolf = isGolfFormat(tournament);
    const _isGolfPickem = isGolfPickem(tournament);
    const _isGolfDraft = _isGolf && !_isGolfPickem;
    const _isPotPickem = tournament.pickem?.mode === "pot";
    const golfStrokes = tournament.golf?.scoring?.power_pick_strokes_per_round ?? 2;
    const lock_triggered = _isGolf
        ? !!tournament.golf?.r1_started
        : _isSeason
        ? !!tournament.round_started?.group
        : !!(
              tournament.round_started &&
              ["r64", "r32", "r16", "qf", "sf", "final", "champion"].some(
                  (s) => tournament.round_started[s]
              )
          );
    const settingsLocked = isLocked || lock_triggered;
    const lockedCopy = _isGolf
        ? "Settings locked — Round 1 has teed off."
        : _isSeason
        ? "Settings locked — first fixture has kicked off."
        : "Settings locked — first knockout round has started.";
    const explainerCopy = _isGolfPickem
        ? "Each crewmate nominates ONE of their golfers for a strokes handicap: the strokes below are shaved off EVERY round that golfer actually plays (miss cut = 2 rounds, make cut = 4). Applies only to the crewmate who picked them. Locks when Round 1 tees off."
        : _isGolfDraft
        ? "Each crewmate nominates ONE of their drafted golfers for a points multiplier: every point that golfer earns (make cut + finish position) is multiplied. Applies only to the crewmate who picked them. Locks when Round 1 tees off."
        : _isSeason
        ? _isPotPickem
            // iter87.308 (follow-up sweep, same class as iter87.306's Pot
            // 4 bug) — this used to hardcode "five clubs"/3 pot values
            // regardless of template, which was both wrong (NFL picks
            // eight teams across 4 pots, not five) and stale (soccer's
            // OWN values are 1×/1.5×/2×, but this said so even for NFL,
            // whose real values are 1×/1.25×/1.5×/2× — visible right
            // below in the correctly template-aware tier list). Reuses
            // `_potListText`, which was already computed correctly per
            // template but never actually wired into this copy.
            ? `Each crewmate nominates ONE of their ${_isNfl ? "eight picked teams" : "five picked clubs"} for a season-long multiplier set by its POT (${_potListText}). Applies to every W/D/L point that ${_isNfl ? "team" : "club"} earns. Locks automatically at first kickoff.`
            : "Each crewmate nominates ONE of their drafted teams for a season-long scoring multiplier. Applies to every W/D/L point that team earns across the whole season. Locks automatically when the first fixture kicks off."
        : _isFootballPO
        ? "Each crewmate nominates ONE of their picked teams for a multiplier set by its POT — Pot 1 teams aren't eligible (no multiplier), Pot 2 = 1.25×, Pot 3 = 1.5×. Applies to every round-win point that team earns. Locks automatically when the first playoff game kicks off."
        : "Each crewmate nominates ONE of their drafted teams (still alive at the start of knockout) for a knockout-only scoring multiplier. Locks automatically when the first knockout round starts.";
    const flatHelperCopy = _isGolfDraft
        ? "Applied to every point your power-picked golfer earns (make cut + finish position)."
        : _isSeason
        ? "Applied to your power-picked team's TOTAL season W/D/L points."
        : "Applied to every crewmate's power-picked team during knockout rounds.";
    const handicapCopyPrefix = _isSeason
        ? "At the moment the first fixture kicks off, each crewmate's rank on the leaderboard locks their multiplier:"
        : "At the moment the first knockout round begins, each crewmate's rank on the leaderboard locks their multiplier:";

    const save = async (patch) => {
        if (settingsLocked) {
            toast.error(lock_triggered
                ? lockedCopy
                : "Tournament is locked");
            return;
        }
        setBusy(true);
        try {
            await api.updatePowerPickSettings(tournament.id, patch);
            toast.success("Power Pick settings saved");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Save failed");
        }
        setBusy(false);
    };

    return (
        <div
            data-testid="power-pick-settings-card"
            className={`brutal-card p-5 space-y-3 ${enabled ? "border-volt bg-volt/5" : "border-[#333]"}`}
        >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className={`w-9 h-9 border-2 border-black shadow-brutal flex items-center justify-center shrink-0 ${enabled ? "bg-volt" : "bg-zinc-800"}`}>
                        <Zap className={`w-4 h-4 ${enabled ? "text-black" : "text-zinc-500"}`} strokeWidth={2.5} />
                    </div>
                    <div className="min-w-0">
                        <p className="font-display font-extrabold text-lg leading-tight">Power Pick · Get the Edge</p>
                        <p className="text-[10px] uppercase tracking-widest text-zinc-500">
                            {_isGolfPickem ? "Strokes handicap — not a multiplier" : _isGolfDraft ? "Points multiplier — locks at first tee" : _isSeason ? "Optional season multiplier" : "Optional knockout multiplier"}
                        </p>
                    </div>
                </div>
                <button
                    type="button"
                    data-testid="power-pick-enabled-toggle"
                    disabled={busy || settingsLocked}
                    onClick={() => save({ enabled: !enabled })}
                    className={`pill shrink-0 font-bold ${enabled ? "bg-volt/20 border-volt text-volt" : "bg-ink border-[#555] text-zinc-400"}`}
                >
                    {busy ? "…" : enabled ? "ON ⚡" : "OFF"}
                </button>
            </div>

            {/* Explainer */}
            <p className="text-[11px] text-zinc-400 leading-relaxed">
                {explainerCopy} <span className="text-volt font-bold">Tap a crewmate's power pick in their breakdown to spot who's riding what.</span>
            </p>

            {settingsLocked && (
                <p className="text-[11px] text-heat flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    {lock_triggered
                        ? lockedCopy
                        : "Tournament is locked — no setting changes."}
                </p>
            )}

            {/* iter86.31.1 — Golf pick'em: single control — strokes shaved
                per round played (1–5). No modes, no multipliers. */}
            {enabled && _isGolfPickem && (
                <div className="brutal-card-light p-3 space-y-2" data-testid="golf-power-pick-strokes-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Strokes shaved · per round played</p>
                    <div className="flex items-center gap-2">
                        <select
                            data-testid="golf-power-pick-strokes-select"
                            disabled={busy || settingsLocked}
                            value={golfStrokes}
                            onChange={(e) => save({ strokes_per_round: parseInt(e.target.value) })}
                            className="brutal-btn-ghost text-sm py-2 px-3 bg-ink border-[#333] cursor-pointer disabled:opacity-50"
                        >
                            {[1, 2, 3, 4, 5].map(n => (
                                <option key={n} value={n}>−{n} stroke{n > 1 ? "s" : ""} / round</option>
                            ))}
                        </select>
                        <p className="text-[11px] text-zinc-400">
                            e.g. −{golfStrokes}/round → miss cut = −{golfStrokes * 2} total, make cut = −{golfStrokes * 4} total.
                        </p>
                    </div>
                </div>
            )}

            {/* iter86.41 — Season Pot Pick'em: multiplier is fixed by the
                POT of the nominated club — no modes to configure. */}
            {enabled && _isPotPickem && (
                <div className="brutal-card-light p-3 border-cyber/40 space-y-1.5" data-testid="power-pick-pot-mode-section">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-cyber">Pot-based multiplier (fixed for this format)</p>
                    <p className="text-[11px] text-zinc-400 leading-relaxed">
                        The multiplier is set by which POT your nominated {_isNfl ? "team" : "club"} came from — the deeper the pot, the bigger the reward:
                    </p>
                    <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-3">
                        {_isNfl ? (
                            <>
                                <li>🏆 <span className="font-bold">Pot 1 · Contenders</span> → <span className="font-mono font-bold">{_pm[1]}×</span></li>
                                <li>🏃 <span className="font-bold">Pot 2 · Dark horses</span> → <span className="font-mono font-bold">{_pm[2]}×</span></li>
                                <li>🎯 <span className="font-bold">Pot 3 · Longshots</span> → <span className="font-mono font-bold">{_pm[3]}×</span></li>
                                <li>🚀 <span className="font-bold text-volt">Pot 4 · Moonshots</span> → <span className="font-mono font-bold">{_pm[4]}×</span></li>
                            </>
                        ) : _isFootballPO ? (
                            <>
                                <li className="opacity-50"><span className="font-bold">Pot 1</span> → <span className="font-mono font-bold">Not eligible</span></li>
                                <li>🏃 <span className="font-bold">Pot 2</span> → <span className="font-mono font-bold">{_pm[2]}×</span></li>
                                <li>🎯 <span className="font-bold text-volt">Pot 3</span> → <span className="font-mono font-bold">{_pm[3]}×</span></li>
                            </>
                        ) : (
                            <>
                                <li>🏆 <span className="font-bold">Pot 1 · title contenders</span> → <span className="font-mono font-bold">{_pm[1]}×</span></li>
                                <li>🏃 <span className="font-bold">Pot 2 · chasing pack</span> → <span className="font-mono font-bold">{_pm[2]}×</span></li>
                                <li>🎯 <span className="font-bold text-volt">Pot 3 · rest of the table</span> → <span className="font-mono font-bold">{_pm[3]}×</span></li>
                            </>
                        )}
                    </ul>
                    <p className="text-[10px] text-zinc-500 italic">Snapshotted at first kickoff. Back a longshot for the big multiplier — or play it safe up top.</p>
                </div>
            )}

            {/* Mode picker — football/season formats only. Golf position
                draft is flat-multiplier only (no handicap quartiles). */}
            {enabled && !_isGolfPickem && !_isPotPickem && (
                <>
                    {!_isGolfDraft && (
                    <div className="brutal-card-light p-3 space-y-2">
                        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Multiplier mode</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <button
                                data-testid="power-pick-mode-flat-btn"
                                disabled={busy || settingsLocked}
                                onClick={() => save({ mode: "flat" })}
                                className={`p-3 border-2 rounded-md text-left transition-all disabled:opacity-50 ${
                                    mode === "flat" ? "border-volt bg-volt/10 shadow-brutal" : "border-[#333] bg-ink hover:border-volt"
                                }`}
                            >
                                <p className="text-sm font-bold">Flat multiplier</p>
                                <p className="text-[10px] text-zinc-400 mt-0.5">Everyone gets the same multiplier you choose below.</p>
                            </button>
                            <button
                                data-testid="power-pick-mode-handicap-btn"
                                disabled={busy || settingsLocked}
                                onClick={() => save({ mode: "handicap" })}
                                className={`p-3 border-2 rounded-md text-left transition-all disabled:opacity-50 ${
                                    mode === "handicap" ? "border-volt bg-volt/10 shadow-brutal" : "border-[#333] bg-ink hover:border-volt"
                                }`}
                            >
                                <p className="text-sm font-bold">Handicap (comeback)</p>
                                <p className="text-[10px] text-zinc-400 mt-0.5">Bottom 25% → 2× · Middle 50% → 1.75× · Top 25% → 1.5×</p>
                            </button>
                        </div>
                    </div>
                    )}

                    {/* Flat multiplier dropdown */}
                    {(mode === "flat" || _isGolfDraft) && (
                        <div className="brutal-card-light p-3 space-y-2">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Multiplier value</p>
                            <div className="flex items-center gap-2">
                                <select
                                    data-testid="power-pick-flat-multiplier-select"
                                    disabled={busy || settingsLocked}
                                    value={flat}
                                    onChange={(e) => save({ flat_multiplier: parseFloat(e.target.value) })}
                                    className="brutal-btn-ghost text-sm py-2 px-3 bg-ink border-[#333] cursor-pointer disabled:opacity-50"
                                >
                                    <option value={1.5}>1.5×</option>
                                    <option value={2.0}>2×</option>
                                    <option value={3.0}>3×</option>
                                </select>
                                <p className="text-[11px] text-zinc-400">{flatHelperCopy}</p>
                            </div>
                        </div>
                    )}

                    {/* Handicap mode summary */}
                    {mode === "handicap" && !_isGolfDraft && (
                        <div className="brutal-card-light p-3 border-cyber/40 space-y-1.5">
                            <p className="text-[10px] uppercase tracking-widest font-bold text-cyber">How handicap mode works</p>
                            <p className="text-[11px] text-zinc-400 leading-relaxed">
                                {handicapCopyPrefix}
                            </p>
                            <ul className="text-[11px] text-zinc-300 space-y-0.5 ml-3">
                                <li>🐢 <span className="font-bold text-volt">Bottom 25%</span> (laggards) → <span className="font-mono font-bold">2×</span></li>
                                <li>🚶 <span className="font-bold">Middle 50%</span> → <span className="font-mono font-bold">1.75×</span></li>
                                <li>🐇 <span className="font-bold">Top 25%</span> (leaders) → <span className="font-mono font-bold">1.5×</span></li>
                            </ul>
                            <p className="text-[10px] text-zinc-500 italic">Rivalries with ≤4 crewmates skip the quartile split — everyone gets 1.75×. Ties at boundaries share the more generous tier (pro-comeback).</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
